/**
 * A sheet — the pilot's `.sheet`: a panel that rises from the bottom of a
 * phone and sits centred, capped at its width, on a wide screen.
 *
 * Built from what the act sheet and the notification feed already did by
 * hand, so both now share one set of behaviours instead of two partial ones
 * (the act sheet locked the scroll but was not modal; the feed was modal but
 * let the page scroll and did not answer Escape):
 *
 *  - `role="dialog"`, `aria-modal`, and a name (`label` or `labelledBy`).
 *  - **Focus moves in, is trapped, and goes back** to whatever opened it —
 *    a keyboard user who closes an act lands on the act they opened, not at
 *    the top of a 60-act day.
 *  - **Escape and the scrim both close.** With sheets stacked, Escape closes
 *    only the top one.
 *  - The page behind does not scroll while one is open.
 *  - Safe-area padding at the bottom, logical properties throughout (RTL),
 *    and its rise is an animation the reduced-motion rule switches off.
 *
 * The look is the pilot's and lives in `components.css` (`.ui-sheet*`); a
 * screen whose sheet differs passes a class of its own.
 */
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useUiT } from './text';

function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', 'audio[controls]', 'video[controls]',
  '[contenteditable]:not([contenteditable="false"])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** What Tab can reach inside `root`, in order. */
export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((el) => !el.hasAttribute('disabled') && el.tabIndex >= 0 && !el.closest('[hidden],[inert]'));
}

/** Open sheets, oldest first — only the last one answers Escape. */
const openSheets: object[] = [];

/** How many sheets hold the page still, and what `overflow` was before the first. */
let scrollLocks = 0;
let overflowBefore = '';

/**
 * Hold the page still under a sheet or a dialog; the returned function lets
 * go. Counted, so a dialog over a sheet does not free the page when it closes.
 */
export function lockScroll(): () => void {
  if (scrollLocks++ === 0) {
    overflowBefore = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    if (--scrollLocks === 0) document.body.style.overflow = overflowBefore;
  };
}

export function Sheet({
  label, labelledBy, onClose, children, className, scrimClassName, closeLabel, modal = true,
}: {
  /**
   * False: a panel BESIDE the page, not over it — no scrim, the page still scrolls and is pressed, Tab walks out of
   * it; Escape and its own close still close it (the act card docked on a computer, the user, 2026-11-02: "don't lock
   * the scrolling, and let the user switch artists without closing the panel").
   */
  modal?: boolean;
  /** The sheet's accessible name. Give this or `labelledBy`. */
  label?: string;
  /** The id of a heading inside the sheet that names it. */
  labelledBy?: string;
  onClose: () => void;
  children: ReactNode;
  /** A screen's own look for the panel, on top of `.ui-sheet`. */
  className?: string;
  /** …and for the scrim, on top of `.ui-sheet-scrim`. */
  scrimClassName?: string;
  /** The scrim's name, if the caller has its own word for it; else the design system's "Close". */
  closeLabel?: string;
}) {
  const tr = useUiT();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const modalNow = useRef(modal);
  modalNow.current = modal;

  // Scroll lock, Escape, and focus — for as long as the sheet is mounted.
  useEffect(() => {
    const token = {};
    openSheets.push(token);

    const unlock = modalNow.current ? lockScroll() : () => {};

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = panel.current;
    // An `autoFocus` inside wins; otherwise the panel itself, so a screen
    // reader starts at the sheet's name rather than at its first button.
    if (node && !node.contains(document.activeElement)) node.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openSheets[openSheets.length - 1] !== token) return;
      close.current();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      openSheets.splice(openSheets.indexOf(token), 1);
      unlock();
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  /** Tab and Shift+Tab go round inside the sheet, never out behind it. */
  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab' || !panel.current || !modalNow.current) return;
    const items = focusables(panel.current);
    if (items.length === 0) { e.preventDefault(); panel.current.focus(); return; }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }

  return (
    <>
      {/* Out of the tab order: Escape is the keyboard's way out, this is the thumb's. */}
      {modal && <button
        type="button"
        tabIndex={-1}
        onClick={() => close.current()}
        aria-label={closeLabel ?? tr('Close')}
        className={cx('ui-sheet-scrim', scrimClassName)}
      />}
      <div
        ref={panel}
        role="dialog"
        aria-modal={modal ? 'true' : undefined}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cx('ui-sheet', className)}
      >
        {children}
      </div>
    </>
  );
}
